import { 
  ISendMessageBody, 
  answerNewChatQuestion, 
  answerRegularQuestion, 
  updateCredits, 
  updateCreditLimit, 
  IUserDetail, 
  EMessageType,
  IGenerateImageBody,
  TSendMessageBody,
  EMessageViewType,
  updateNewRegularQuestion,
  updatedRegularQuestion,
  SetFileItems
} from "redux/actions";


// Track the current message state
let currentState = {
  messageId: undefined as number | undefined,
  chatId: undefined as number | undefined,
  latestPrompt: '',
  hasContent: false,
  messageContent: '',
  receivedInProgress: false, // Flag to track if we've received in_progress events
  userMessageSent: false, // Flag to track if a user message was sent
  thinkingContent: '',  // New property to store thinking content
  hasThinkingContent: false, 


  // Add context tracking for markdown elements
  inListItem: false,    // Are we inside a list item
  inCodeBlock: false,   // Are we inside a code block
  listLevel: 0,         // Tracking list nesting level

  // Smart chunking state
  chunkBuffer: '',
  lastUpdateTime: Date.now(),
  tokenCount: 0,
  mdElementCount: 0, // Tracks markdown elements in current buffer
  openMdElements: [] as string[], // Stack of open markdown elements

    // Table tracking
    inTable: false,
    tableRowCount: 0,
    currentTableRow: '',  // Formatting blocks tracker
    formattingBlocks: {
      inTable: false,
      tableContent: '',
      inCodeBlock: false,
      codeBlockContent: '',
      inBold: false,
      boldContent: '',
      inItalic: false,
      italicContent: '',
      inList: false,
      listContent: '',
      lastBlockType: null as string | null
    }
};

// Handler for user message created events - shows initial "thinking" state
export const handleUserMessageCreated = (
  eventContent: string,
  data: ISendMessageBody | IGenerateImageBody | TSendMessageBody
) => {
  try {
    const userData = JSON.parse(eventContent);
    
    // Set flag that user has sent a message
    currentState.userMessageSent = true;
    
    // Display initial "thinking" message right away
    const messageParams = {
      type: "assistant" as EMessageType,
      content: "Evaluating your request...", // Default content before in_progress events
      isNew: true,
      isProcessing: true,
      images: []
    };
    
    if (data.messageViewType === "new") {
      answerNewChatQuestion(messageParams);
    } else {
      answerRegularQuestion(messageParams);
    }
    
    return userData;
  } catch (e) {
    console.error('Error parsing user message data:', e);
    return null;
  }
};

// Handler for assistant message created events
export const handleAssistantMessageCreated = (
    eventContent: string,
    data: ISendMessageBody | TSendMessageBody
  ) => {
    try {
      const assistantMessageData = JSON.parse(eventContent);
      // Reset tracking variables for new message
      currentState.hasContent = false;
      currentState.messageContent = '';
      
      // Store the message ID and chat ID
      currentState.messageId = assistantMessageData.id;
      currentState.chatId = assistantMessageData.chat_id;
      // Initialize the assistant message with empty content
      const messageParams = {
        type: "assistant" as EMessageType,
        content: "",
        isNew: true,
        chat_id: assistantMessageData.chat_id,
        id: assistantMessageData.id,
        images: [],
        model_id: assistantMessageData.model_id,
        prompt: currentState.latestPrompt || undefined,
        // thread_id: assistantMessageData.chat_id
      };

      const images = data.images && data?.images.map((img) => {
        return {
          id: 1,
          model_type: '',
          model_id: 1,
          name: null,
          path: img,
          size: '',
          created_at: '',
          updated_at: '',
        }
      });

      const updateNewChat = {
        type: "user" as EMessageType,
        id: assistantMessageData.id?? 1,
        chat_id:  assistantMessageData.chat_id?? 1,
        images: data?.images ? images ? images : [] : [],
      }

      if (data.messageViewType === "new") {
        updateNewRegularQuestion(updateNewChat)
        answerNewChatQuestion(messageParams);
      } else {
        updatedRegularQuestion(updateNewChat)
        answerRegularQuestion(messageParams);
      }
      
      return assistantMessageData;
    } catch (e) {
      console.error('Error parsing assistant message data:', e);
      return null;
    }
  };

// Handler for in progress events - updates message with progress
export const handleInProgress = (
  eventContent: string,
  data: ISendMessageBody | TSendMessageBody
) => {

  currentState.receivedInProgress = true;
  // Use the in_progress content as message content
  const messageParams = {
    type: "assistant" as EMessageType,
    content: eventContent || "Processing...", // Use the event content
    isNew: false,
    isProcessing: true,
    images: [],
    id: currentState.messageId,
    chat_id: currentState.chatId
  };
  
  if (data.messageViewType === "new") {
    answerNewChatQuestion(messageParams);
  } else {
    answerRegularQuestion(messageParams);
  }
  
  return eventContent;
};

// Handler for completed events - finalize the message with the complete content
export const handleCompleted = (
  eventContent: string,
  data: ISendMessageBody | TSendMessageBody,
  admin_team_deleted: boolean
) => {
  if (!admin_team_deleted) {
    
    const cleanContent = removeQuotesFromData(eventContent);

    const messageParams = {
      type: "assistant" as EMessageType,
      content: cleanContent, // Use the complete content from the completed event
      isNew: false,
      isComplete: true, // Mark as complete
      isStreaming: false,
      isProcessing: false,
      images: [],
      id: currentState.messageId, 
      chat_id: currentState.chatId
    };
    
    if (data.messageViewType === "new") {
      answerNewChatQuestion(messageParams);
    } else {
      answerRegularQuestion(messageParams);
    }
    
    // Reset state for next message after a short delay
    setTimeout(() => {
      resetMessageTracking();
    }, 100);
  }
  
  return eventContent;
};

const removeQuotesFromData = (eventContent: string) => {
  // Skip if content is empty
  if (!eventContent) {
    return eventContent;
  }
  
  // For string data (most message and completed events)
  if (typeof eventContent === 'string') {
    // Remove surrounding quotes if they exist
    if (eventContent.startsWith('"') && eventContent.endsWith('"')) {
      return eventContent.substring(1, eventContent.length - 1);
    }
  }
  
  return eventContent;
};

// Enhanced handleMessage that intelligently groups formatting chunks
export const handleMessage = (
  eventContent: string,
  data: ISendMessageBody | TSendMessageBody,
  admin_team_deleted: boolean
) => {
  if (admin_team_deleted) {
    return eventContent;
  }

    // Remove quotes from the event content
    const cleanContent = removeQuotesFromData(eventContent);
  
    // Skip truly empty tokens
    if (cleanContent === '') {
      return cleanContent;
    }
  
    appendToMessageContent(cleanContent);
    return cleanContent;
  
  // Helper function to append content with proper spacing
function appendToMessageContent(content: string | Record<string, any>) {
  // Skip truly empty tokens
  if (content === '') {
    return;
  }

  // For text tokens, handle special formatting
  if (typeof content === 'string') {
    // Preserve newlines exactly as they appear in the token
    currentState.messageContent += content;
    
    // Update formatting context tracking (optional enhancement)
    if (content.includes('\n')) {
      // Reset any line-based formatting context
      currentState.inListItem = content.endsWith('\n- ');
      
      // Track code blocks if needed
      const codeBlockStarts = (content.match(/```/g) || []).length;
      if (codeBlockStarts % 2 !== 0) {
        currentState.inCodeBlock = !currentState.inCodeBlock;
      }
    }
  } else if (typeof content === 'object' && content !== null && 'content' in content) {
    // If it's a structured message object with content property
    currentState.messageContent = content.content;
  }
  
  // Send an update with the current accumulated content
  sendUpdate();
}
  function sendUpdate() {
    const messageParams = {
      type: "assistant" as EMessageType,
      content: currentState.messageContent,
      isNew: false,
      isStreaming: true,
      isProcessing: false,
      images: [],
      id: currentState.messageId,
      chat_id: currentState.chatId
    };
    
    if (data.messageViewType === "new") {
      answerNewChatQuestion(messageParams);
    } else {
      answerRegularQuestion(messageParams);
    }
  }
  
};

export const handleImageMessage = (
  eventContent: string,
  data: ISendMessageBody ,
  admin_team_deleted: boolean) => {
    setTimeout(() => {
  if (!admin_team_deleted) {

    const imagesData = JSON.parse(eventContent);
    // Skip truly empty tokens
    if (eventContent === '') {
      return eventContent;
    }
    
    // Mark that we have content now
    currentState.hasContent = true;
    
    // Send the FULL accumulated content each time
    // This ensures correct formatting during streaming
    const messageParams = {
      type: "assistant" as EMessageType,
      content: "",
      isNew: false,
      isStreaming: true,
      isProcessing: false,
      images: imagesData.image_links,
      id: currentState.messageId,
      chat_id: currentState.chatId
    };
    if (data.messageViewType === "new") {
      answerNewChatQuestion(messageParams);
    } else {
      answerRegularQuestion(messageParams);
    }
  }
  
  return eventContent;
}, 160);
}

// Reset function for starting a new conversation
export const resetMessageTracking = () => {
  currentState = {      
    messageId: undefined,
    chatId: undefined,
    latestPrompt: '',
    hasContent: false,
    messageContent: '',
    receivedInProgress: false,
    userMessageSent: false,
    thinkingContent: '',  // Reset thinking content
    hasThinkingContent: false,  // Reset thinking flag

     // Reset formatting context
     inListItem: false,
     inCodeBlock: false,
     listLevel: 0,

 
  // Smart chunking state
  chunkBuffer: '',
  lastUpdateTime: Date.now(),
  tokenCount: 0,
  mdElementCount: 0, // Tracks markdown elements in current buffer
  openMdElements: [] as string[], // Stack of open markdown elements

    // Table tracking
    inTable: false,
    tableRowCount: 0,
    currentTableRow: '',

    // Formatting blocks tracker
    formattingBlocks: {
      inTable: false,
      tableContent: '',
      inCodeBlock: false,
      codeBlockContent: '',
      inBold: false,
      boldContent: '',
      inItalic: false,
      italicContent: '',
      inList: false,
      listContent: '',
      lastBlockType: null
    },

  };
};

export const handleFilesUploaded = (eventContent: string) => {
  try {
    const filesData = JSON.parse(eventContent);
    const {documents, images} = filesData;
    const combinedFiles = [...images, ...documents];
    SetFileItems(combinedFiles);
    return filesData;
  } catch (e) {
    console.error('Error parsing files data:', e);
    return null;
  }
};

export const handleChatUpdated = (
  eventContent: string,
  isPrivateChat: { current: boolean },
  chatIdRef: React.MutableRefObject<number>
) => {
  try {
    const chatData = JSON.parse(eventContent);
    chatIdRef.current = chatData.id;
    
    if (chatData.is_private !== undefined) {
      isPrivateChat.current = chatData.is_private;
    }
    
    return chatData;
  } catch (e) {
    console.error('Error parsing chat data:', e);
    return null;
  }
};

export const handleCreditsUpdated = (
  eventContent: string,
  userDetail: IUserDetail | null
) => {
  try {
    const creditsData = JSON.parse(eventContent);
    
    if (userDetail?.user.team.role === "admin") {
      updateCredits(creditsData.credits);
    } else {
      updateCreditLimit({
        daily_limit: creditsData.daily_limit,
        used_today: creditsData.used_today,
      });
    }
    
    return creditsData;
  } catch (e) {
    console.error('Error parsing credits data:', e);
    return null;
  }
};

export const handleChatStarted = (eventContent: string) => {
  return eventContent;
};

export const handleImagesGenerated = (
  eventContent: string,
  data: IGenerateImageBody ,
  admin_team_deleted: boolean
) => {

  setTimeout(() => {
  if (!admin_team_deleted) {
   
    const imagesData = JSON.parse(eventContent);
       
    const messageParams = {
      type: "assistant" as EMessageType,
      content: "", // Use the complete content from the completed event
      isNew: false,
      // isComplete: true, // Mark as complete
      isStreaming: false,
      isProcessing: false,
      images: imagesData.images,
      id: currentState.messageId, 
      chat_id: currentState.chatId
    };
    
    if (data.messageViewType === "new") {
      answerNewChatQuestion(messageParams);
    } else {
      answerRegularQuestion(messageParams);
    }
  }
  
  return eventContent;
}, 160)
};

export const handleErrorMessage = (
  eventContent: string,
  data: ISendMessageBody | TSendMessageBody
) => {
  setTimeout(() => {
  const errorMessage = String(eventContent || "").trim();
  const messageParams = {
    type: "assistant" as EMessageType,
    content: "", // Use the complete content from the completed event
    isNew: false,
    isStreaming: false,
    isProcessing: false,
    images: [],
    id: currentState.messageId, 
    chat_id: currentState.chatId,
    err_message: errorMessage,
  };
  
  if (data.messageViewType === "new") {
    answerNewChatQuestion(messageParams);
  } else {
    answerRegularQuestion(messageParams);
  }

return eventContent;
},155);
}

// Handler for thinking events - displays the thinking process
export const handleThinking = (
  eventContent: string,
  data: ISendMessageBody | TSendMessageBody,
  admin_team_deleted: boolean
) => {
    if (!admin_team_deleted) {
      // Skip truly empty tokens
      if (eventContent === '') {
        return eventContent;
      }
      if (!currentState.hasThinkingContent) {
        currentState.thinkingContent = "Thinking: " + eventContent;
        currentState.hasThinkingContent = true;
      } else {
        currentState.thinkingContent += eventContent;
      }
      const messageParams = {
        type: "assistant" as EMessageType,
        content: currentState.thinkingContent,
        isNew: false,
        isThinking: true,  // Special flag for styling thinking content differently
        isStreaming: true,
        isProcessing: false,
        images: [],
        id: currentState.messageId,
        chat_id: currentState.chatId
      };
      
      if (data.messageViewType === "new") {
        answerNewChatQuestion(messageParams);
      } else {
        answerRegularQuestion(messageParams);
      }
    }
    
    return eventContent;
};

// Handler for in progress events - updates message with progress
export const handleImgInProgress = (
  eventContent: string,
  data: ISendMessageBody 
) => {
  setTimeout(() => {
  // Set flag that we've received an in_progress event
  currentState.receivedInProgress = true;
  
  // Use the in_progress content as message content
  const messageParams = {
    type: "assistant" as EMessageType,
    content: eventContent || "Processing...", // Use the event content
    isNew: false,
    isProcessing: true,
    images: [],
    id: currentState.messageId,
    chat_id: currentState.chatId
  };
  
  if (data.messageViewType === "new") {
    answerNewChatQuestion(messageParams);
  } else {
    answerRegularQuestion(messageParams);
  }
  
  return eventContent;
}, 150);
};

export const handleThreadUpdated = (
  eventContent: string,
  data: ISendMessageBody | TSendMessageBody
) => {
  try {
    const assistantMessageData = JSON.parse(eventContent);
    // Reset tracking variables for new message
    currentState.hasContent = false;
    currentState.messageContent = '';
    
    // Store the message ID and chat ID
    currentState.messageId = assistantMessageData.id;
    currentState.chatId = assistantMessageData.chat_id;
    // Initialize the assistant message with empty content
    const messageParams = {
      type: "assistant" as EMessageType,
      content: "",
      isNew: true,
      chat_id: assistantMessageData.chat_id,
      id: assistantMessageData.id,
      images: [],
      // model_id: assistantMessageData.model_id,
      prompt: currentState.latestPrompt || undefined,
      thread_id: assistantMessageData.id
    };

    const images = data.images && data?.images.map((img) => {
      return {
        id: 1,
        model_type: '',
        model_id: 1,
        name: null,
        path: img,
        size: '',
        created_at: '',
        updated_at: '',
      }
    });

    const updateNewChat = {
      type: "user" as EMessageType,
      id: assistantMessageData.id?? 1,
      thread_id: assistantMessageData.id ?? null,
      images: data?.images ? images ? images : [] : [],
    }
    
    if (data.messageViewType === "new") {
      updateNewRegularQuestion(updateNewChat)
      answerNewChatQuestion(messageParams);
    } else {
      updatedRegularQuestion(updateNewChat)
      answerRegularQuestion(messageParams);
    }
    
    return assistantMessageData;
  } catch (e) {
    console.error('Error parsing assistant message data:', e);
    return null;
  }
}

// Handler for in progress events - updates message with progress
export const handleGenerateQues = (
  eventContent: string,
  data: EMessageViewType | undefined,
  chatId: number | null
) => {
  currentState.receivedInProgress = true;
  
  // Use the in_progress content as message content
  const messageParams = {
    type: "assistant" as EMessageType,
    regenerateInProg: eventContent || "Processing...", // Use the event content
    isNew: false,
    images: [],
    id: currentState.messageId,
    chat_id: chatId?? 1
  };
  
  if (data === "new") {
    answerNewChatQuestion(messageParams);
  } else {
    answerRegularQuestion(messageParams);
  }
  
  return eventContent;
};

export const handleInComplete = (
  eventContent: string,
  data: ISendMessageBody | TSendMessageBody,
) => {
  const messageParams = {
    type: "assistant" as EMessageType,
    images: [],
    TokenExpired: eventContent,
  };
  
  if (data.messageViewType === "new") {
    answerNewChatQuestion(messageParams);
  } else {
    answerRegularQuestion(messageParams);
  }
      // Reset state for next message after a short delay
  setTimeout(() => {
    resetMessageTracking();
  }, 100);

  return eventContent;
}

export const handleCitition = (
  eventContent: string[],
  data: ISendMessageBody | TSendMessageBody
) => {

  const messageParams = {
    type: "assistant" as EMessageType,
    isNew: false,
    isStreaming: false,
    isProcessing: false,
    images: [],
    id: currentState.messageId, 
    chat_id: currentState.chatId,
    citations: eventContent,
  };
  
  if (data.messageViewType === "new") {
    answerNewChatQuestion(messageParams);
  } else {
    answerRegularQuestion(messageParams);
  }

  return eventContent;
}
